import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { shiftService, employeeService, stationPreferenceService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import useIsMobile from '../../hooks/useIsMobile';
import { C } from '../../styles/theme';

// ─── Design Tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

const STATION_STYLES = {
    Kitchen:    { bg: '#fffbeb', border: '#d97706', text: '#92400e' },
    Bar:        { bg: '#eff6ff', border: '#2563eb', text: '#1e3a8a' },
    Cashier:    { bg: '#eff5ff', border: '#2563eb', text: '#1e3a8a' },
    FOH:        { bg: '#f5f3ff', border: '#7c3aed', text: '#4c1d95' },
    Unassigned: { bg: '#ecfeff', border: '#0891b2', text: '#164e63' },
};

const DEFAULT_STATIONS = ['Kitchen', 'Bar', 'Cashier', 'FOH'];

// ─── Avatar helpers ────────────────────────────────────────────────────────────
const PALETTE = ['#2563eb','#2563eb','#d97706','#7c3aed','#0891b2','#be185d','#059669','#dc2626'];
function avatarColor(name) { return PALETTE[(name || '').charCodeAt(0) % PALETTE.length]; }

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
}

// ─── ISO week helpers (Monday-based) ───────────────────────────────────────────
function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function getWeekDates(week, year) {
    const jan4 = new Date(year, 0, 4);
    const daysFromMonday = (jan4.getDay() + 6) % 7;
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - daysFromMonday);
    const targetMonday = new Date(week1Monday);
    targetMonday.setDate(week1Monday.getDate() + (week - 1) * 7);
    return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(targetMonday);
        d.setDate(targetMonday.getDate() + i);
        return d;
    });
}

function calculateHours(start, end) {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins === 0) return 0;
    if (mins < 0) mins += 24 * 60;
    return Math.round(mins / 30) * 0.5;
}

function formatDateRange(dates) {
    if (!dates || dates.length < 7) return '';
    const opts = { day: 'numeric', month: 'short' };
    const start = dates[0].toLocaleDateString('en-GB', opts);
    const end = dates[6].toLocaleDateString('en-GB', opts);
    return `${start} — ${end}`;
}

function toLocalIso(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function formatFullDate(date) {
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

const DAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── Shift status helpers ──────────────────────────────────────────────────────
function isShiftInProgress(shift, todayIso) {
    if (shift.date !== todayIso) return false;
    if (shift.status === 'In Progress' || shift.status === 'Clocked In') return true;
    // Check by time
    const now = new Date();
    const [sh, sm] = (shift.start_time || '00:00').split(':').map(Number);
    const [eh, em] = (shift.end_time || '23:59').split(':').map(Number);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    if (endMins <= startMins) endMins += 24 * 60;
    return nowMins >= startMins && nowMins < endMins;
}

function isNoShow(shift, todayIso) {
    if (shift.status !== 'Scheduled') return false;
    if (shift.date >= todayIso) return false;
    return true;
}

// ─── Popover shared styles ────────────────────────────────────────────────────
const POPOVER_BACKDROP = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.18)', zIndex: 3000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const POPOVER_CARD = {
    background: C.white, borderRadius: 14, border: `1px solid ${C.border}`,
    boxShadow: '0 12px 40px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)',
    padding: 24, minWidth: 340, maxWidth: 400, fontFamily: FONT,
};
const POP_LABEL = {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: 0.5, color: '#94a3b8', marginBottom: 4,
};
const POP_INPUT = {
    width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`,
    borderRadius: 8, fontSize: 12, fontFamily: FONT, outline: 'none',
    boxSizing: 'border-box',
};
const POP_BTN_CANCEL = {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: '#f1f5f9', color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const POP_BTN_SAVE = {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: C.green, color: C.white, fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const POP_BTN_DELETE = {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: C.red, color: C.white, fontSize: 12, fontWeight: 600, cursor: 'pointer',
};

// ─── ShiftPopover (create) ────────────────────────────────────────────────────
const ShiftPopover = ({ empId, date, employees, stations, shifts, weekIsos, shiftMap, overtimeThreshold, getEmployeeWeekHours, onSave, onClose, t }) => {
    const [selectedEmpId, setSelectedEmpId] = useState(empId || (employees[0]?.employee_id || null));
    const [selectedDate, setSelectedDate] = useState(date || toLocalIso(new Date()));

    // Determine most-used station for the employee
    const getMostUsedStation = (eid) => {
        const empShifts = shifts.filter(s => s.employee_id === eid && s.station);
        if (empShifts.length === 0) return stations[0] || 'Kitchen';
        const counts = {};
        empShifts.forEach(s => { counts[s.station] = (counts[s.station] || 0) + 1; });
        return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
    };

    const [station, setStation] = useState(() => getMostUsedStation(selectedEmpId));
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('17:00');

    // Fetch station preference and use as default (overrides most-used)
    useEffect(() => {
        if (!selectedEmpId) return;
        stationPreferenceService.get(selectedEmpId).then(pref => {
            if (pref?.primary_station) {
                setStation(pref.primary_station);
            } else {
                setStation(getMostUsedStation(selectedEmpId));
            }
        }).catch(() => {
            setStation(getMostUsedStation(selectedEmpId));
        });
    }, [selectedEmpId]);

    const hours = calculateHours(startTime, endTime);
    const emp = employees.find(e => e.employee_id === selectedEmpId);

    // Conflict detection
    const existingShifts = shifts.filter(s => s.employee_id === selectedEmpId && s.date === selectedDate);
    const conflicts = existingShifts.filter(s => {
        const [ss, se] = [s.start_time, s.end_time];
        if (!ss || !se || !startTime || !endTime) return false;
        return startTime < se && endTime > ss;
    });

    // Overtime preview
    const currentWeekHours = selectedEmpId ? getEmployeeWeekHours(selectedEmpId) : 0;
    const projectedHours = currentWeekHours + hours;
    const isOvertime = weekIsos.includes(selectedDate) && projectedHours > overtimeThreshold;

    const handleSubmit = () => {
        onSave({
            employee_id: selectedEmpId,
            date: selectedDate,
            station,
            start_time: startTime,
            end_time: endTime,
            expected_hours: hours,
        });
    };

    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    return (
        <div style={POPOVER_BACKDROP} onClick={onClose}>
            <div style={POPOVER_CARD} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 20 }}>
                    {t('add_shift')}
                </div>

                {/* Employee selector (or display-only if empId was provided) */}
                <div style={{ marginBottom: 14 }}>
                    <div style={POP_LABEL}>{t('employee')}</div>
                    {empId ? (
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{emp?.name || '-'}</div>
                    ) : (
                        <select
                            value={selectedEmpId || ''}
                            onChange={e => setSelectedEmpId(Number(e.target.value))}
                            style={{ ...POP_INPUT, cursor: 'pointer' }}
                        >
                            {employees.map(e => (
                                <option key={e.employee_id} value={e.employee_id}>{e.name}</option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Date selector (or display-only if date was provided) */}
                <div style={{ marginBottom: 14 }}>
                    <div style={POP_LABEL}>{t('date')}</div>
                    {date ? (
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{selectedDate}</div>
                    ) : (
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={e => setSelectedDate(e.target.value)}
                            style={POP_INPUT}
                        />
                    )}
                </div>

                {/* Station */}
                <div style={{ marginBottom: 14 }}>
                    <div style={POP_LABEL}>{t('station')}</div>
                    <select value={station} onChange={e => setStation(e.target.value)} style={{ ...POP_INPUT, cursor: 'pointer' }}>
                        {stations.map(st => (
                            <option key={st} value={st}>{st}</option>
                        ))}
                    </select>
                </div>

                {/* Times */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                    <div style={{ flex: 1 }}>
                        <div style={POP_LABEL}>{t('start_time')}</div>
                        <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={POP_INPUT} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={POP_LABEL}>{t('end_time')}</div>
                        <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={POP_INPUT} />
                    </div>
                </div>

                {/* Calculated hours */}
                <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={POP_LABEL}>{t('hours')}</div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{hours}h</span>
                </div>

                {/* Conflict warning */}
                {conflicts.length > 0 && (
                    <div style={{
                        background: C.amberLight, border: `1px solid ${C.amber}`, borderRadius: 8,
                        padding: '8px 12px', marginBottom: 14, fontSize: 12, color: '#92400e',
                    }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: 6 }}>
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        {conflicts.map((c, i) => (
                            <span key={i}>
                                {t('shift_conflict_warning')}: {c.start_time?.slice(0, 5)}-{c.end_time?.slice(0, 5)} ({c.station || t('unassigned')})
                                {i < conflicts.length - 1 ? '; ' : ''}
                            </span>
                        ))}
                    </div>
                )}

                {/* Overtime warning */}
                {isOvertime && (
                    <div style={{
                        background: C.amberLight, border: `1px solid ${C.amber}`, borderRadius: 8,
                        padding: '8px 12px', marginBottom: 14, fontSize: 12, color: '#92400e',
                    }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: 6 }}>
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {t('overtime_warning')}: {projectedHours}h / {overtimeThreshold}h
                    </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                    <button onClick={onClose} style={POP_BTN_CANCEL}>{t('cancel')}</button>
                    <button onClick={handleSubmit} style={POP_BTN_SAVE}>{t('add_shift')}</button>
                </div>
            </div>
        </div>
    );
};

// ─── EditPopover ──────────────────────────────────────────────────────────────
const EditPopover = ({ shift, employees, stations, todayIso, onSave, onDelete, onClose, onReload, t }) => {
    const [mode, setMode] = useState(null); // null | 'editTime' | 'changeStation'
    const [startTime, setStartTime] = useState(shift.start_time?.slice(0, 5) || '09:00');
    const [endTime, setEndTime] = useState(shift.end_time?.slice(0, 5) || '17:00');
    const [station, setStation] = useState(shift.station || stations[0]);
    const [clockLoading, setClockLoading] = useState(false);

    const emp = employees.find(e => e.employee_id === shift.employee_id);
    const empName = emp?.name || t('unknown');
    const hours = calculateHours(startTime, endTime);
    const s = STATION_STYLES[shift.station] || STATION_STYLES.Unassigned;

    const isTodayOrPast = shift.date <= todayIso;
    const isScheduled = shift.status === 'Scheduled';
    const isInProgress = shift.status === 'In Progress' || shift.status === 'Clocked In';

    const handleSaveTime = () => {
        onSave({
            shift_id: shift.shift_id,
            employee_id: shift.employee_id,
            date: shift.date,
            station: shift.station,
            start_time: startTime,
            end_time: endTime,
            expected_hours: hours,
            status: shift.status,
        });
    };

    const handleSaveStation = () => {
        onSave({
            shift_id: shift.shift_id,
            employee_id: shift.employee_id,
            date: shift.date,
            station,
            start_time: shift.start_time,
            end_time: shift.end_time,
            expected_hours: shift.expected_hours,
            status: shift.status,
        });
    };

    const [clockInTime, setClockInTime] = useState(shift.start_time?.slice(0, 5) || '09:00');
    const [showClockInTime, setShowClockInTime] = useState(false);

    const handleClockIn = async () => {
        setClockLoading(true);
        try {
            await shiftService.managerClockIn(shift.shift_id, clockInTime);
            await onReload();
            onClose();
        } catch (err) {
            console.error('Clock-in failed', err);
        } finally {
            setClockLoading(false);
        }
    };

    const [editClockInTime, setEditClockInTime] = useState(shift.actual_start_time?.slice(0, 5) || shift.start_time?.slice(0, 5) || '09:00');
    const [clockOutTime, setClockOutTime] = useState(() => {
        const now = new Date();
        const nowHHMM = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
        const schedEnd = shift.end_time?.slice(0, 5) || '17:00';
        return nowHHMM < schedEnd ? nowHHMM : schedEnd;
    });
    const [showClockOutTime, setShowClockOutTime] = useState(false);

    const handleClockOut = async () => {
        setClockLoading(true);
        try {
            await shiftService.managerClockOut(shift.shift_id, clockOutTime);
            await onReload();
            onClose();
        } catch (err) {
            console.error('Clock-out failed', err);
        } finally {
            setClockLoading(false);
        }
    };

    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const actionBtnStyle = {
        width: '100%', padding: '9px 12px', borderRadius: 8,
        border: `1px solid ${C.border}`, background: C.white,
        fontSize: 12, fontWeight: 600, color: C.text,
        cursor: 'pointer', textAlign: 'left',
        display: 'flex', alignItems: 'center', gap: 8,
    };

    return (
        <div style={POPOVER_BACKDROP} onClick={onClose}>
            <div style={{ ...POPOVER_CARD, minWidth: 320, maxWidth: 380 }} onClick={e => e.stopPropagation()}>

                {/* Shift summary header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.border, flexShrink: 0 }} />
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                            {shift.start_time?.slice(0, 5)} - {shift.end_time?.slice(0, 5)}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted }}>
                            {empName} &middot; {shift.date} &middot; {shift.actual_hours != null
                                ? <span style={{ color: C.green, fontWeight: 600 }}>{shift.actual_hours}h worked</span>
                                : `${shift.expected_hours || calculateHours(shift.start_time, shift.end_time)}h scheduled`}
                        </div>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                        <span style={{
                            fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                            background: s.bg, color: s.text, border: `1px solid ${s.border}`,
                        }}>
                            {shift.station || t('unassigned')}
                        </span>
                    </div>
                </div>

                {/* Quick actions (default view) */}
                {mode === null && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button style={actionBtnStyle} onClick={() => setMode('editTime')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            {t('edit_shift_times')}
                        </button>
                        <button style={actionBtnStyle} onClick={() => setMode('changeStation')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                            {t('change_station')}
                        </button>
                        <button style={{ ...actionBtnStyle, color: C.red }} onClick={() => onDelete(shift)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            {t('delete_shift')}
                        </button>

                        {/* Manager clock-in/out (only for today/past) */}
                        {isTodayOrPast && isScheduled && !showClockInTime && (
                            <button
                                style={{ ...actionBtnStyle, color: C.green, borderColor: C.greenBorder }}
                                onClick={() => setShowClockInTime(true)}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                                {t('mark_clocked_in')}
                            </button>
                        )}
                        {isTodayOrPast && isScheduled && showClockInTime && (
                            <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: C.mutedLight }}>
                                    {t('clock_in')} — {t('edit_actual_times')}
                                </div>
                                <div style={{ fontSize: 11, color: C.muted }}>
                                    {t('scheduled')}: {shift.start_time?.slice(0,5)} - {shift.end_time?.slice(0,5)}
                                </div>
                                <input
                                    type="time"
                                    value={clockInTime}
                                    onChange={(e) => setClockInTime(e.target.value)}
                                    style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: 'inherit' }}
                                />
                                <button
                                    style={{ ...actionBtnStyle, background: C.green, color: C.white, borderColor: C.green, justifyContent: 'center' }}
                                    onClick={handleClockIn}
                                    disabled={clockLoading}
                                >
                                    {clockLoading ? '...' : `${t('mark_clocked_in')} (${clockInTime})`}
                                </button>
                            </div>
                        )}
                        {isTodayOrPast && isInProgress && !showClockOutTime && (
                            <button
                                style={{ ...actionBtnStyle, color: C.amber, borderColor: C.amber }}
                                onClick={() => setShowClockOutTime(true)}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.amber} strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                                {t('mark_clocked_out')}
                            </button>
                        )}
                        {isTodayOrPast && isInProgress && showClockOutTime && (() => {
                            const actualHours = calculateHours(editClockInTime, clockOutTime);
                            const scheduledHours = calculateHours(shift.start_time?.slice(0, 5) || '09:00', shift.end_time?.slice(0, 5) || '17:00');
                            const isOvertime = actualHours > scheduledHours;
                            return (
                                <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: C.mutedLight }}>
                                        {t('edit_actual_times')}
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, marginBottom: 4 }}>{t('clock_in')}</div>
                                            <input
                                                type="time"
                                                value={editClockInTime}
                                                onChange={(e) => setEditClockInTime(e.target.value)}
                                                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: 'inherit' }}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, marginBottom: 4 }}>{t('clock_out')}</div>
                                            <input
                                                type="time"
                                                value={clockOutTime}
                                                onChange={(e) => setClockOutTime(e.target.value)}
                                                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: 'inherit' }}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f8fafc', borderRadius: 6, fontSize: 12 }}>
                                        <span style={{ color: C.muted }}>{t('calculated_hours')}</span>
                                        <span style={{ fontWeight: 700, color: isOvertime ? C.amber : C.green }}>{actualHours}h
                                            {isOvertime && <span style={{ fontWeight: 400, color: C.amber }}> ({t('scheduled')}: {scheduledHours}h)</span>}
                                        </span>
                                    </div>
                                    <button
                                        style={{ ...actionBtnStyle, background: C.amber, color: C.white, borderColor: C.amber, justifyContent: 'center' }}
                                        onClick={handleClockOut}
                                        disabled={clockLoading}
                                    >
                                        {clockLoading ? '...' : `${t('mark_clocked_out')} (${clockOutTime})`}
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* Edit time inline */}
                {mode === 'editTime' && (
                    <div>
                        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                            <div style={{ flex: 1 }}>
                                <div style={POP_LABEL}>{t('start_time')}</div>
                                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={POP_INPUT} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={POP_LABEL}>{t('end_time')}</div>
                                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={POP_INPUT} />
                            </div>
                        </div>
                        <div style={{ marginBottom: 14, fontSize: 12, color: C.muted }}>
                            {t('hours')}: <strong style={{ color: C.text }}>{hours}h</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={() => setMode(null)} style={POP_BTN_CANCEL}>{t('cancel')}</button>
                            <button onClick={handleSaveTime} style={POP_BTN_SAVE}>{t('save')}</button>
                        </div>
                    </div>
                )}

                {/* Change station inline */}
                {mode === 'changeStation' && (
                    <div>
                        <div style={{ marginBottom: 14 }}>
                            <div style={POP_LABEL}>{t('station')}</div>
                            <select value={station} onChange={e => setStation(e.target.value)} style={{ ...POP_INPUT, cursor: 'pointer' }}>
                                {stations.map(st => (
                                    <option key={st} value={st}>{st}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={() => setMode(null)} style={POP_BTN_CANCEL}>{t('cancel')}</button>
                            <button onClick={handleSaveStation} style={POP_BTN_SAVE}>{t('save')}</button>
                        </div>
                    </div>
                )}

                {/* Close row (when in default mode) */}
                {mode === null && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                        <button onClick={onClose} style={POP_BTN_CANCEL}>{t('close')}</button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── DeleteConfirm ────────────────────────────────────────────────────────────
const DeleteConfirm = ({ shift, employees, onConfirm, onClose, t }) => {
    const emp = employees.find(e => e.employee_id === shift.employee_id);
    const empName = emp?.name || t('unknown');
    const s = STATION_STYLES[shift.station] || STATION_STYLES.Unassigned;

    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    return (
        <div style={POPOVER_BACKDROP} onClick={onClose}>
            <div style={{ ...POPOVER_CARD, minWidth: 320, maxWidth: 380 }} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 8 }}>
                    {t('delete_shift')}
                </div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                    {t('delete_shift_confirm')}
                </div>
                <div style={{
                    background: C.redLight, borderRadius: 8, padding: '10px 14px', marginBottom: 20,
                    display: 'flex', alignItems: 'center', gap: 10,
                }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.border, flexShrink: 0 }} />
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{empName}</div>
                        <div style={{ fontSize: 12, color: C.muted }}>
                            {shift.date} &middot; {shift.start_time?.slice(0, 5)}-{shift.end_time?.slice(0, 5)} &middot; {shift.station || t('unassigned')}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button onClick={onClose} style={POP_BTN_CANCEL}>{t('cancel')}</button>
                    <button onClick={onConfirm} style={POP_BTN_DELETE}>{t('delete')}</button>
                </div>
            </div>
        </div>
    );
};

// ─── CopyWeekModal ────────────────────────────────────────────────────────────
const CopyWeekModal = ({ currentWeek, currentYear, shifts, copying, onConfirm, onClose, t }) => {
    const srcWeek = currentWeek === 1 ? 52 : currentWeek - 1;
    const srcYear = currentWeek === 1 ? currentYear - 1 : currentYear;

    const srcDates = getWeekDates(srcWeek, srcYear);
    const tgtDates = getWeekDates(currentWeek, currentYear);

    const srcIsos = srcDates.map(d => toLocalIso(d));
    const tgtIsos = tgtDates.map(d => toLocalIso(d));

    const srcShiftCount = shifts.filter(s => srcIsos.includes(s.date)).length;
    const tgtExistingCount = shifts.filter(s => tgtIsos.includes(s.date)).length;

    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    return (
        <div style={POPOVER_BACKDROP} onClick={onClose}>
            <div
                style={{
                    background: C.white, borderRadius: 16, border: `1px solid ${C.border}`,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)',
                    padding: 28, width: 480, maxWidth: '95vw', fontFamily: FONT,
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>{t('copy_last_week')}</div>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 4, display: 'flex', alignItems: 'center' }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>

                {/* Source → Target cards */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    {/* Source card */}
                    <div style={{ flex: 1, background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.mutedLight, marginBottom: 6 }}>
                            {t('source_week')}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                            {t('week')} {srcWeek}{srcYear !== currentYear ? ` (${srcYear})` : ''}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
                            {formatDateRange(srcDates)}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.green }}>
                            {srcShiftCount} {t('shifts')}
                        </div>
                    </div>

                    {/* Arrow */}
                    <div style={{ color: C.mutedLight, flexShrink: 0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </div>

                    {/* Target card */}
                    <div style={{ flex: 1, border: `2px dashed ${C.green}`, background: '#eff5ff', borderRadius: 10, padding: '14px 16px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.green, opacity: 0.7, marginBottom: 6 }}>
                            {t('target_week')}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                            {t('week')} {currentWeek}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
                            {formatDateRange(tgtDates)}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.green }}>
                            {srcShiftCount} {t('shifts_will_be_created')}
                        </div>
                    </div>
                </div>

                {/* Info text */}
                <div style={{
                    background: C.greenLight, border: `1px solid ${C.greenBorder}`, borderRadius: 8,
                    padding: '10px 14px', fontSize: 12, color: '#166534', marginBottom: 20,
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span>
                        {tgtExistingCount > 0
                            ? t('existing_shifts_kept_with_count').replace('{count}', tgtExistingCount)
                            : t('existing_shifts_kept')}
                    </span>
                </div>

                {/* Confirm button */}
                <button
                    onClick={onConfirm}
                    disabled={copying || srcShiftCount === 0}
                    style={{
                        width: '100%', padding: '11px 0', borderRadius: 10, border: 'none',
                        background: (copying || srcShiftCount === 0) ? '#86efac' : C.green,
                        color: C.white, fontSize: 14, fontWeight: 700, cursor: (copying || srcShiftCount === 0) ? 'not-allowed' : 'pointer',
                        fontFamily: FONT, transition: 'background 0.15s',
                    }}
                >
                    {copying ? t('copying') : `${t('copy')} ${srcShiftCount} ${t('shifts')}`}
                </button>
            </div>
        </div>
    );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const PortalSchedulePage = () => {
    const { getTenantSetting, tenantConfig } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();

    // ── State ──────────────────────────────────────────────────────────────────
    const [employees, setEmployees] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [currentWeek, setCurrentWeek] = useState(() => getWeekNumber(new Date()));
    const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
    const [shiftPopover, setShiftPopover] = useState({ open: false, empId: null, date: null });
    const [editPopover, setEditPopover] = useState({ open: false, shift: null });
    const [copyModal, setCopyModal] = useState(false);
    const [copying, setCopying] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ open: false, shift: null });
    const [stationFilter, setStationFilter] = useState(null);
    const [hoveredCell, setHoveredCell] = useState(null);
    const isMobile = useIsMobile();
    const [selectedDayIdx, setSelectedDayIdx] = useState(() => (new Date().getDay() + 6) % 7); // mobile day-view (Mon=0)

    // ── Derived ────────────────────────────────────────────────────────────────
    const stations = tenantConfig?.stations || DEFAULT_STATIONS;
    const overtimeThreshold = getTenantSetting('payroll.overtime_threshold_hours_week', 40);
    const todayIso = useMemo(() => toLocalIso(new Date()), []);
    const todayWeek = useMemo(() => getWeekNumber(new Date()), []);
    const isCurrentWeek = currentWeek === todayWeek && currentYear === new Date().getFullYear();

    const weekDates = useMemo(() => getWeekDates(currentWeek, currentYear), [currentWeek, currentYear]);
    const weekIsos = useMemo(() => weekDates.map(d => toLocalIso(d)), [weekDates]);

    // Group shifts by employee+date for O(1) lookup
    const shiftMap = useMemo(() => {
        const map = {};
        shifts.forEach(s => {
            const key = `${s.employee_id}_${s.date}`;
            if (!map[key]) map[key] = [];
            map[key].push(s);
        });
        return map;
    }, [shifts]);

    // ── Computed stats ─────────────────────────────────────────────────────────
    const weekShifts = useMemo(() => shifts.filter(s => weekIsos.includes(s.date)), [shifts, weekIsos]);

    const stats = useMemo(() => {
        const totalShifts = weekShifts.length;
        const staffScheduled = new Set(weekShifts.map(s => s.employee_id)).size;
        const totalHours = Math.round(weekShifts.reduce((sum, s) =>
            sum + (s.actual_hours || s.expected_hours || calculateHours(s.start_time, s.end_time)), 0) * 100) / 100;

        // Overtime warnings
        const empHours = {};
        weekShifts.forEach(s => {
            const h = s.actual_hours || s.expected_hours || calculateHours(s.start_time, s.end_time);
            empHours[s.employee_id] = (empHours[s.employee_id] || 0) + h;
        });
        const overtimeCount = Object.values(empHours).filter(h => h > overtimeThreshold).length;

        return { totalShifts, staffScheduled, totalHours, overtimeCount };
    }, [weekShifts, overtimeThreshold]);

    // ── Employee hours ─────────────────────────────────────────────────────────
    const getEmployeeWeekHours = useCallback((empId) => {
        const raw = weekIsos.reduce((sum, iso) => {
            const dayShifts = shiftMap[`${empId}_${iso}`] || [];
            return sum + dayShifts.reduce((s, sh) =>
                s + (sh.actual_hours || sh.expected_hours || calculateHours(sh.start_time, sh.end_time)), 0);
        }, 0);
        return Math.round(raw * 100) / 100;
    }, [weekIsos, shiftMap]);

    function getHoursColor(hours) {
        if (hours > overtimeThreshold) return C.red;
        if (hours >= overtimeThreshold * 0.9) return C.amber;
        return C.text;
    }

    // ── Coverage computation ───────────────────────────────────────────────────
    const computeCoverage = useCallback((iso) => {
        const dayShifts = shifts.filter(s => s.date === iso);
        const filledStations = new Set(dayShifts.map(s => s.station).filter(Boolean));
        return {
            filled: stations.filter(st => filledStations.has(st)),
            missing: stations.filter(st => !filledStations.has(st)),
            count: filledStations.size,
            total: stations.length,
        };
    }, [shifts, stations]);

    // ── Data loading ───────────────────────────────────────────────────────────
    const loadData = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const [empData, shiftData] = await Promise.all([
                employeeService.getAllEmployees(),
                shiftService.getAllShifts(),
            ]);
            setEmployees((empData || []).filter(e => e.status?.toLowerCase() === 'active'));
            setShifts(shiftData || []);
        } catch (err) {
            setError(t('failed_to_load_schedule'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { loadData(); }, [loadData]);

    // Auto-dismiss success toasts
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    // ── CRUD handlers ──────────────────────────────────────────────────────────
    const handleSaveShift = async (formData) => {
        try {
            if (formData.shift_id) {
                await shiftService.updateShift(formData.shift_id, formData);
                setSuccess(t('shift_updated'));
            } else {
                await shiftService.createShift(formData);
                setSuccess(t('shift_added'));
            }
            await loadData();
        } catch (err) {
            setError(err.response?.data?.detail || t('error_saving_shift'));
        }
        setShiftPopover({ open: false, empId: null, date: null });
        setEditPopover({ open: false, shift: null });
    };

    const handleDeleteShift = async () => {
        try {
            await shiftService.deleteShift(deleteModal.shift.shift_id);
            setSuccess(t('shift_removed'));
            await loadData();
        } catch (err) {
            setError(err.response?.data?.detail || t('error_deleting_shift'));
        }
        setDeleteModal({ open: false, shift: null });
        setEditPopover({ open: false, shift: null });
    };

    const handleCopyWeek = async () => {
        setCopying(true);
        try {
            const srcWeek = currentWeek === 1 ? 52 : currentWeek - 1;
            const srcYear = currentWeek === 1 ? currentYear - 1 : currentYear;
            const result = await shiftService.copyWeek(srcWeek, currentWeek, srcYear, currentYear);
            setSuccess(`${result.created} ${t('shifts_copied')}${result.skipped > 0 ? `, ${result.skipped} ${t('skipped_duplicates')}` : ''}`);
            await loadData();
            setCopyModal(false);
        } catch (err) {
            setError(err.response?.data?.detail || t('failed_to_copy_shifts'));
        } finally {
            setCopying(false);
        }
    };

    // ── Week navigation ────────────────────────────────────────────────────────
    const goToPrevWeek = () => {
        if (currentWeek <= 1) {
            setCurrentYear(y => y - 1);
            setCurrentWeek(52);
        } else {
            setCurrentWeek(w => w - 1);
        }
    };

    const goToNextWeek = () => {
        if (currentWeek >= 52) {
            setCurrentYear(y => y + 1);
            setCurrentWeek(1);
        } else {
            setCurrentWeek(w => w + 1);
        }
    };

    const goToToday = () => {
        const now = new Date();
        setCurrentWeek(getWeekNumber(now));
        setCurrentYear(now.getFullYear());
    };

    // ── Daily totals for footer ────────────────────────────────────────────────
    const getDayTotals = useCallback((iso) => {
        const dayShifts = shifts.filter(s => s.date === iso);
        const hours = Math.round(dayShifts.reduce((sum, s) =>
            sum + (s.actual_hours || s.expected_hours || calculateHours(s.start_time, s.end_time)), 0) * 100) / 100;
        const staffCount = new Set(dayShifts.map(s => s.employee_id)).size;
        return { hours, staffCount };
    }, [shifts]);

    // ── Filtered employees (by station filter) ────────────────────────────────
    const filteredEmployees = useMemo(() => {
        if (!stationFilter) return employees;
        // Show only employees who have at least one shift with the selected station this week
        return employees.filter(emp =>
            weekShifts.some(s => s.employee_id === emp.employee_id && s.station === stationFilter)
        );
    }, [employees, stationFilter, weekShifts]);

    // ── Check if a date is past ────────────────────────────────────────────────
    const isPast = useCallback((iso) => iso < todayIso, [todayIso]);
    const isToday = useCallback((iso) => iso === todayIso, [todayIso]);

    // Inject pulse animation
    useEffect(() => {
        if (document.getElementById('schedule-pulse-keyframes')) return;
        const style = document.createElement('style');
        style.id = 'schedule-pulse-keyframes';
        style.textContent = `@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`;
        document.head.appendChild(style);
        return () => { style.remove(); };
    }, []);

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT, color: C.text }}>

                {/* Toast notifications */}
                {(error || success) && (
                    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {error && (
                            <div style={{ background: C.redLight, border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: C.red, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>{error}</span>
                                <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.red, padding: 2 }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </button>
                            </div>
                        )}
                        {success && (
                            <div style={{ background: C.greenLight, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: '10px 16px', fontSize: 13, color: C.green, fontWeight: 500 }}>
                                {success}
                            </div>
                        )}
                    </div>
                )}

                {/* ── 1. Page Header ─────────────────────────────────────────── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>{t('schedule')}</h1>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.green, background: C.greenLight, borderRadius: 6, padding: '3px 10px' }}>
                        {t('week')} {currentWeek}
                    </span>
                </div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
                    {formatFullDate(new Date())}
                </div>

                {/* ── 2. Week Navigation Bar ─────────────────────────────────── */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 10, padding: '10px 16px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button onClick={goToPrevWeek} style={{ width: 32, height: 32, borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                        </button>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, minWidth: 160, textAlign: 'center' }}>
                            {formatDateRange(weekDates)}
                        </div>
                        <button onClick={goToNextWeek} style={{ width: 32, height: 32, borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                        {!isCurrentWeek && (
                            <button onClick={goToToday} style={{ fontSize: 12, fontWeight: 600, color: C.green, background: C.greenLight, border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>
                                {t('today')}
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button onClick={() => setCopyModal(true)} style={{ padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white, color: C.text, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            {t('copy_last_week')}
                        </button>
                        <button onClick={() => setShiftPopover({ open: true, empId: null, date: null })} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: C.green, color: C.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            {t('add_shift')}
                        </button>
                    </div>
                </div>

                {/* ── 3. Quick Stats Row ─────────────────────────────────────── */}
                <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? '1fr 1fr' : undefined, gap: 10, marginBottom: 16 }}>
                    {[
                        { label: t('shifts_this_week'), value: stats.totalShifts, color: C.text },
                        { label: t('staff_scheduled'), value: stats.staffScheduled, color: C.text },
                        { label: t('total_hours'), value: `${stats.totalHours}h`, color: C.text },
                        { label: t('overtime_warnings'), value: stats.overtimeCount, color: stats.overtimeCount > 0 ? C.amber : C.text, bg: stats.overtimeCount > 0 ? C.amberLight : undefined },
                    ].map((pill, i) => (
                        <div key={i} style={{ flex: 1, background: pill.bg || C.white, border: `1.5px solid ${pill.bg ? C.amber : C.border}`, borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ fontSize: 20, fontWeight: 800, color: pill.color, lineHeight: 1 }}>{pill.value}</div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{pill.label}</div>
                        </div>
                    ))}
                </div>

                {/* ── 4. Station Legend ───────────────────────────────────────── */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                    {stations.map(st => {
                        const s = STATION_STYLES[st] || STATION_STYLES.Unassigned;
                        const isActive = stationFilter === st;
                        return (
                            <button
                                key={st}
                                onClick={() => setStationFilter(prev => prev === st ? null : st)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '5px 12px', borderRadius: 20,
                                    border: `1.5px solid ${isActive ? s.border : C.border}`,
                                    background: isActive ? s.bg : C.white,
                                    cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                    color: isActive ? s.text : C.muted,
                                    transition: 'all 0.15s',
                                }}
                            >
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.border }} />
                                {t(st)}
                            </button>
                        );
                    })}
                    {stationFilter && (
                        <button
                            onClick={() => setStationFilter(null)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                padding: '5px 12px', borderRadius: 20,
                                border: `1.5px solid ${C.border}`, background: C.white,
                                cursor: 'pointer', fontSize: 12, fontWeight: 500, color: C.muted,
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            {t('clear_filter')}
                        </button>
                    )}
                </div>

                {/* ── 5. Loading State ────────────────────────────────────────── */}
                {loading && (
                    <div style={{ background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, padding: 48, textAlign: 'center', color: C.mutedLight, fontSize: 14 }}>
                        {t('loading_schedule')}
                    </div>
                )}

                {/* ── 6. Empty state: no employees ───────────────────────────── */}
                {!loading && employees.length === 0 && (
                    <div style={{ background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, padding: 48, textAlign: 'center' }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: C.greenLight, margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                            {t('no_employees_found')}
                        </div>
                        <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
                            {t('add_employees_before_scheduling')}
                        </div>
                        <button
                            onClick={() => navigate('/portal/employees')}
                            style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: C.green, color: C.white, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        >
                            {t('go_to_employees')}
                        </button>
                    </div>
                )}

                {/* ── 7. Grid Card (desktop) ───────────────────────────────────── */}
                {!loading && employees.length > 0 && !isMobile && (
                    <div style={{ background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                      <div className="portal-hscroll">
                        <div className="portal-schedule-min">

                        {/* ── Day Headers ──────────────────────────────────── */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '160px repeat(7, 1fr) 72px',
                            borderBottom: `1.5px solid ${C.border}`,
                            background: C.bg,
                        }}>
                            <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center' }}>
                                {t('employee')}
                            </div>
                            {weekDates.map((date, i) => {
                                const iso = weekIsos[i];
                                const today = isToday(iso);
                                return (
                                    <div key={i} style={{ padding: '8px 6px', textAlign: 'center', borderLeft: `1px solid ${C.border}` }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: today ? C.green : C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            {DAY_NAMES_SHORT[i]}
                                        </div>
                                        <div style={{
                                            fontSize: 16, fontWeight: 700,
                                            color: today ? C.white : C.text,
                                            lineHeight: 1.4,
                                            ...(today ? {
                                                background: C.green,
                                                borderRadius: '50%',
                                                width: 28, height: 28,
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            } : {}),
                                        }}>
                                            {date.getDate()}
                                        </div>
                                    </div>
                                );
                            })}
                            <div style={{ padding: '10px 6px', textAlign: 'center', borderLeft: `1px solid ${C.border}`, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {t('hours')}
                            </div>
                        </div>

                        {/* ── Coverage Bar ─────────────────────────────────── */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '160px repeat(7, 1fr) 72px',
                            borderBottom: `1px solid ${C.border}`,
                            background: '#fafbfc',
                        }}>
                            <div style={{ padding: '6px 14px', fontSize: 10, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center' }}>
                                {t('coverage')}
                            </div>
                            {weekIsos.map((iso, i) => {
                                const cov = computeCoverage(iso);
                                const hasGaps = cov.count < cov.total;
                                return (
                                    <div key={i} style={{ padding: '6px 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, borderLeft: `1px solid ${C.border}` }}>
                                        {cov.filled.map(st => {
                                            const s = STATION_STYLES[st] || STATION_STYLES.Unassigned;
                                            return <div key={st} style={{ width: 6, height: 6, borderRadius: '50%', background: s.border }} />;
                                        })}
                                        <span style={{ fontSize: 10, fontWeight: 600, color: hasGaps ? C.amber : C.green, marginLeft: 2 }}>
                                            {cov.count}/{cov.total}
                                        </span>
                                    </div>
                                );
                            })}
                            <div style={{ borderLeft: `1px solid ${C.border}` }} />
                        </div>

                        {/* ── Employee Rows ────────────────────────────────── */}
                        {filteredEmployees.length === 0 && stationFilter && (
                            <div style={{ padding: '24px 14px', textAlign: 'center', color: C.mutedLight, fontSize: 13 }}>
                                {t('no_employees_for_station')}
                            </div>
                        )}

                        {filteredEmployees.map((emp, empIdx) => {
                            const empHours = getEmployeeWeekHours(emp.employee_id);
                            const hoursColor = getHoursColor(empHours);

                            return (
                                <div
                                    key={emp.employee_id}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '160px repeat(7, 1fr) 72px',
                                        borderBottom: empIdx < filteredEmployees.length - 1 ? `1px solid #f1f5f9` : 'none',
                                        minHeight: 56,
                                    }}
                                >
                                    {/* Employee info cell */}
                                    <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8, borderRight: `1px solid ${C.border}` }}>
                                        <div style={{
                                            width: 28, height: 28, borderRadius: '50%',
                                            background: avatarColor(emp.name),
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 10, fontWeight: 700, color: C.white, flexShrink: 0,
                                        }}>
                                            {getInitials(emp.name)}
                                        </div>
                                        <div style={{ overflow: 'hidden' }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {emp.name}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                                                {emp.role && (
                                                    <span style={{ fontSize: 10, color: C.mutedLight }}>{emp.role}</span>
                                                )}
                                                {emp.pay_type && (
                                                    <span style={{
                                                        fontSize: 9, fontWeight: 600,
                                                        color: emp.pay_type === 'salary' ? '#2563eb' : C.amber,
                                                        background: emp.pay_type === 'salary' ? '#eff6ff' : C.amberLight,
                                                        borderRadius: 3, padding: '1px 5px',
                                                    }}>
                                                        {emp.pay_type === 'salary' ? t('salary') : t('hourly')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 7 day cells */}
                                    {weekIsos.map((iso, dayIdx) => {
                                        const cellKey = `${emp.employee_id}_${iso}`;
                                        const cellShifts = shiftMap[cellKey] || [];
                                        const past = isPast(iso);
                                        const today = isToday(iso);
                                        const hovered = hoveredCell === cellKey;

                                        return (
                                            <div
                                                key={dayIdx}
                                                style={{
                                                    padding: '4px 3px',
                                                    borderLeft: `1px solid ${C.border}`,
                                                    background: today ? '#f0fdf8' : 'transparent',
                                                    cursor: cellShifts.length === 0 && !past ? 'pointer' : 'default',
                                                    position: 'relative',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 3,
                                                    justifyContent: cellShifts.length === 0 ? 'center' : 'flex-start',
                                                    alignItems: 'stretch',
                                                    minHeight: 52,
                                                }}
                                                onMouseEnter={() => setHoveredCell(cellKey)}
                                                onMouseLeave={() => setHoveredCell(null)}
                                                onClick={() => {
                                                    if (cellShifts.length === 0 && !past) {
                                                        setShiftPopover({ open: true, empId: emp.employee_id, date: iso });
                                                    }
                                                }}
                                            >
                                                {/* Empty cell */}
                                                {cellShifts.length === 0 && (
                                                    past ? (
                                                        <div style={{ textAlign: 'center', fontSize: 10, color: '#cbd5e1', fontWeight: 500 }}>
                                                            {t('off')}
                                                        </div>
                                                    ) : (
                                                        hovered && (
                                                            <div style={{ textAlign: 'center', color: C.green, opacity: 0.6 }}>
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                                            </div>
                                                        )
                                                    )
                                                )}

                                                {/* Shift blocks */}
                                                {cellShifts.map(shift => {
                                                    const s = STATION_STYLES[shift.station] || STATION_STYLES.Unassigned;
                                                    const inProgress = shift.status === 'In Progress';
                                                    const completed = shift.status === 'Completed';
                                                    const noShow = isNoShow(shift, todayIso);
                                                    const dimmed = stationFilter && shift.station !== stationFilter;

                                                    const borderColor = noShow ? C.red : inProgress ? C.green : completed ? '#6b7280' : s.border;

                                                    return (
                                                        <div
                                                            key={shift.shift_id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditPopover({ open: true, shift });
                                                            }}
                                                            style={{
                                                                background: noShow ? C.redLight : completed ? '#f9fafb' : inProgress ? '#eff5ff' : s.bg,
                                                                border: `1.5px solid ${borderColor}`,
                                                                borderRadius: 6,
                                                                padding: '3px 5px',
                                                                cursor: 'pointer',
                                                                opacity: dimmed ? 0.3 : 1,
                                                                transition: 'opacity 0.15s, box-shadow 0.15s',
                                                                position: 'relative',
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                <span style={{ fontSize: 10, fontWeight: 600, color: noShow ? C.red : s.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {shift.start_time?.slice(0, 5)} - {shift.end_time?.slice(0, 5)}
                                                                </span>
                                                            </div>
                                                            <div style={{ fontSize: 9, fontWeight: 600, color: noShow ? C.red : s.text, opacity: 0.75, marginTop: 1 }}>
                                                                {shift.station || t('unassigned')}
                                                            </div>
                                                            {/* Status badge */}
                                                            {inProgress && (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                                                                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, animation: 'pulse 2s infinite' }} />
                                                                    <span style={{ fontSize: 8, fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                                                        {t('in_progress')}{shift.actual_start_time ? ` ${shift.actual_start_time.slice(0,5)}` : ''}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {completed && (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                                                                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                                                                    <span style={{ fontSize: 8, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                                                        {shift.actual_start_time && shift.actual_end_time
                                                                            ? `${shift.actual_start_time.slice(0,5)} - ${shift.actual_end_time.slice(0,5)}`
                                                                            : t('completed')}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {noShow && (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                                                                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.red }} />
                                                                    <span style={{ fontSize: 8, fontWeight: 700, color: C.red, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                                                        {t('no_show')}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}

                                    {/* Hours column */}
                                    <div style={{
                                        padding: '8px 6px',
                                        borderLeft: `1px solid ${C.border}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 13, fontWeight: 700, color: hoursColor,
                                    }}>
                                        {empHours > 0 ? `${empHours}h` : '-'}
                                    </div>
                                </div>
                            );
                        })}

                        {/* ── Footer: daily totals ─────────────────────────── */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '160px repeat(7, 1fr) 72px',
                            borderTop: `1.5px solid ${C.border}`,
                            background: C.bg,
                        }}>
                            <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center' }}>
                                {t('daily_totals')}
                            </div>
                            {weekIsos.map((iso, i) => {
                                const totals = getDayTotals(iso);
                                return (
                                    <div key={i} style={{ padding: '8px 6px', textAlign: 'center', borderLeft: `1px solid ${C.border}` }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{totals.hours > 0 ? `${totals.hours}h` : '-'}</div>
                                        <div style={{ fontSize: 10, color: C.mutedLight }}>{totals.staffCount > 0 ? `${totals.staffCount} ${t('staff')}` : ''}</div>
                                    </div>
                                );
                            })}
                            <div style={{
                                padding: '8px 6px', textAlign: 'center', borderLeft: `1px solid ${C.border}`,
                                fontSize: 12, fontWeight: 800, color: C.green,
                            }}>
                                {stats.totalHours > 0 ? `${stats.totalHours}h` : '-'}
                            </div>
                        </div>
                        </div>
                      </div>
                    </div>
                )}

                {/* ── 7b. Mobile day view ──────────────────────────────────────── */}
                {!loading && employees.length > 0 && isMobile && (
                    <div>
                        {/* Day selector */}
                        <div className="portal-hscroll" style={{ display: 'flex', gap: 8, marginBottom: 14, paddingBottom: 2 }}>
                            {weekDates.map((date, i) => {
                                const sel = i === selectedDayIdx;
                                const today = isToday(weekIsos[i]);
                                return (
                                    <button key={i} onClick={() => setSelectedDayIdx(i)} style={{
                                        flexShrink: 0, minWidth: 50, padding: '8px 12px', borderRadius: 12, cursor: 'pointer',
                                        border: `1.5px solid ${sel ? C.green : C.border}`, background: sel ? C.green : C.white,
                                        color: sel ? '#fff' : (today ? C.green : C.text), textAlign: 'center', fontFamily: 'inherit',
                                    }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: sel ? 0.9 : 0.65 }}>{DAY_NAMES_SHORT[i]}</div>
                                        <div style={{ fontSize: 17, fontWeight: 800, marginTop: 2 }}>{date.getDate()}</div>
                                    </button>
                                );
                            })}
                        </div>

                        {(() => {
                            const iso = weekIsos[selectedDayIdx];
                            const past = isPast(iso);
                            const cov = computeCoverage(iso);
                            const hasGaps = cov.count < cov.total;
                            return (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '0 2px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                                            {weekDates[selectedDayIdx].toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })}
                                        </div>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: hasGaps ? C.amber : C.green }}>
                                            {cov.count}/{cov.total} {t('covered') || 'covered'}
                                        </span>
                                    </div>

                                    <div style={{ background: C.white, borderRadius: 14, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                                        {filteredEmployees.map((emp, idx) => {
                                            const cellShifts = shiftMap[`${emp.employee_id}_${iso}`] || [];
                                            return (
                                                <div key={emp.employee_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderTop: idx === 0 ? 'none' : '1px solid #f1f5f9' }}>
                                                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: avatarColor(emp.name), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                                                        {getInitials(emp.name)}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.name}</div>
                                                        {emp.role && <div style={{ fontSize: 12, color: C.mutedLight, marginTop: 1 }}>{emp.role}</div>}
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
                                                        {cellShifts.length === 0 ? (
                                                            past ? (
                                                                <span style={{ fontSize: 12, color: '#cbd5e1', fontWeight: 600 }}>{t('off') || 'Off'}</span>
                                                            ) : (
                                                                <button onClick={() => setShiftPopover({ open: true, empId: emp.employee_id, date: iso })}
                                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: C.green, background: '#eff5ff', border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                                                    {t('add') || 'Add'}
                                                                </button>
                                                            )
                                                        ) : (
                                                            cellShifts.map(shift => {
                                                                const s = STATION_STYLES[shift.station] || STATION_STYLES.Unassigned;
                                                                const noShow = isNoShow(shift, todayIso);
                                                                return (
                                                                    <button key={shift.shift_id} onClick={() => setEditPopover({ open: true, shift })}
                                                                        style={{ textAlign: 'right', background: noShow ? C.redLight : s.bg, border: `1.5px solid ${noShow ? C.red : s.border}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                                                        <div style={{ fontSize: 12, fontWeight: 700, color: noShow ? C.red : s.text, whiteSpace: 'nowrap' }}>
                                                                            {shift.start_time?.slice(0, 5)}–{shift.end_time?.slice(0, 5)}
                                                                        </div>
                                                                        <div style={{ fontSize: 10, fontWeight: 600, color: noShow ? C.red : s.text, opacity: 0.8 }}>
                                                                            {shift.station || t('unassigned')}
                                                                        </div>
                                                                    </button>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* ── Empty shifts state (employees exist but no shifts) ───── */}
                {!loading && employees.length > 0 && weekShifts.length === 0 && (
                    <div style={{ background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, padding: 32, textAlign: 'center', marginTop: 16 }}>
                        <div style={{ width: 44, height: 44, borderRadius: '50%', background: C.greenLight, margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                            {t('no_shifts_this_week')}
                        </div>
                        <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                            {t('add_shifts_or_use_week_schedule')}
                        </div>
                        <button
                            onClick={() => setShiftPopover({ open: true, empId: null, date: null })}
                            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: C.green, color: C.white, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        >
                            {t('add_first_shift')}
                        </button>
                    </div>
                )}

                {/* ── ShiftPopover (create new shift) ──────────────── */}
                {shiftPopover.open && (
                    <ShiftPopover
                        empId={shiftPopover.empId}
                        date={shiftPopover.date}
                        employees={employees}
                        stations={stations}
                        shifts={shifts}
                        weekIsos={weekIsos}
                        shiftMap={shiftMap}
                        overtimeThreshold={overtimeThreshold}
                        getEmployeeWeekHours={getEmployeeWeekHours}
                        onSave={handleSaveShift}
                        onClose={() => setShiftPopover({ open: false, empId: null, date: null })}
                        t={t}
                    />
                )}

                {/* ── EditPopover (edit existing shift) ─────────────── */}
                {editPopover.open && editPopover.shift && (
                    <EditPopover
                        shift={editPopover.shift}
                        employees={employees}
                        stations={stations}
                        todayIso={todayIso}
                        onSave={handleSaveShift}
                        onDelete={(shift) => { setEditPopover({ open: false, shift: null }); setDeleteModal({ open: true, shift }); }}
                        onClose={() => setEditPopover({ open: false, shift: null })}
                        onReload={loadData}
                        t={t}
                    />
                )}

                {/* ── DeleteConfirm modal ───────────────────────────── */}
                {deleteModal.open && deleteModal.shift && (
                    <DeleteConfirm
                        shift={deleteModal.shift}
                        employees={employees}
                        onConfirm={handleDeleteShift}
                        onClose={() => setDeleteModal({ open: false, shift: null })}
                        t={t}
                    />
                )}

                {/* ── CopyWeekModal ─────────────────────────────────── */}
                {copyModal && (
                    <CopyWeekModal
                        currentWeek={currentWeek}
                        currentYear={currentYear}
                        shifts={shifts}
                        copying={copying}
                        onConfirm={handleCopyWeek}
                        onClose={() => setCopyModal(false)}
                        t={t}
                    />
                )}

            </div>
        </PortalLayout>
    );
};

export default PortalSchedulePage;
